import {
    moveToFocusedInboxText,
    moveToOtherInboxText,
    alwaysMoveToFocusedInboxText,
    alwaysMoveToOtherInboxText,
    moveToFolderMoveAllFrom,
} from './getMoveToMenuProperties.locstring.json';
import loc from 'owa-localize';
/* tslint:disable:jsx-no-lambda WI:47719 */
import { MOVETO_WIDTH } from '../utils/constants';
import { Icon } from 'office-ui-fabric-react/lib/Icon';
import { ActionSource } from 'owa-analytics-actions';
import { isFeatureEnabled } from 'owa-feature-flags';
import { ShouldShowBehavior } from 'owa-filterable-menu';
import { MenuItemType } from 'owa-mail-filterable-menu-behavior';
import { resetFocus } from 'owa-mail-focus-manager';
import { getCustomIcon } from 'owa-mail-folder-forest-actions';
import { createNewFolder } from 'owa-mail-folder-store/lib/actions/newFolder';
import { getMoveToMenuProps, MoveToMailboxContext, MoveToMenuItem } from 'owa-mail-moveto-control';
import { lazyShowSweepDialog } from 'owa-mail-sweep-view';
import * as lazyTriageActions from 'owa-mail-triage-action';
import { FocusedViewFilter } from 'owa-mail-triage-action';

import * as React from 'react';
import {
    TableView,
    listViewStore,
    isItemPartOperation,
    MailFolderTableQuery,
    getInboxClassificationFilterForTable,
} from 'owa-mail-list-store';

import {
    IContextualMenuProps,
    DirectionalHint,
} from 'office-ui-fabric-react/lib/components/ContextualMenu';
import { lazyMoveRowsToInboxPlusPivot } from 'owa-mail-inbox-plus-actions';
import isInboxPlusEnabled from 'owa-mail-inbox-plus-store/lib/selectors/isInboxPlusEnabled';
import InboxClassificationFilter from 'owa-mail-inbox-plus-store/lib/store/schema/InboxClassificationFilter';
import InboxPlusPivotViewState from 'owa-mail-inbox-plus-store/lib/store/schema/InboxPlusPivotViewState';
import getInboxPlusPivotDisplayProperties from 'owa-mail-inbox-plus-store/lib/selectors/getInboxPlusPivotDisplayProperties';
import inboxPlusStore from 'owa-mail-inbox-plus-store/lib/store/Store';

const styles = require('./MailMoveToMenuStyles.scss');

/**
 * Gets Inbox menu items
 */
function getInboxMenuItems(
    tableView: TableView,
    nonIconClassName: string,
    iconClassName: string,
    onAfterMenuClicked: () => void,
    actionSource: ActionSource,
    shouldShowBehaviorMap: { [id: number]: ShouldShowBehavior }
): JSX.Element[] {
    let items: JSX.Element[] = [];

    // Show pivots if
    // 1. Inbox plus is enabled
    // 2. Not an item part (conversation view not supported yet)
    // 3. Not in the Inbox pivot
    if (
        isInboxPlusEnabled() &&
        !isItemPartOperation() &&
        getInboxClassificationFilterForTable(tableView.id) !== InboxClassificationFilter.None
    ) {
        const mailTableQuery = tableView.tableQuery as MailFolderTableQuery;
        const pivotMenuItemsToShow = inboxPlusStore.inboxPlusPivots.filter(
            (pivot: InboxPlusPivotViewState) =>
                pivot.InboxClassificationFilter !== InboxClassificationFilter.None &&
                pivot.isEnabled &&
                pivot.InboxClassificationFilter !== mailTableQuery.inboxClassificationFilter
        );

        for (const pivotMenuItem of pivotMenuItemsToShow) {
            const pivotDisplayProperties = getInboxPlusPivotDisplayProperties(
                pivotMenuItem.InboxClassificationFilter
            );

            items.push(
                <div
                    key={'moveTo' + pivotDisplayProperties.displayText}
                    role={'menuitemcheckbox'}
                    data-is-focusable={true}
                    className={iconClassName}
                    onClick={async () => {
                        const moveRowsToInboxPlusPivot = await lazyMoveRowsToInboxPlusPivot.import();
                        moveRowsToInboxPlusPivot(
                            [...tableView.selectedRowKeys.keys()],
                            pivotMenuItem.InboxClassificationFilter,
                            tableView.id,
                            actionSource
                        );
                        onAfterMenuClicked();
                    }}
                    title={pivotDisplayProperties.displayText}>
                    <Icon
                        styles={{ root: styles.moveToInboxPlusPivotIcon }}
                        iconName={pivotDisplayProperties.icon}
                    />
                    <span className={styles.moveToFocusedOtherMenuItems}>
                        {pivotDisplayProperties.displayText}
                    </span>
                </div>
            );
        }

        return items;
    }

    // Add move to Focused/Other
    if (shouldShowBehaviorMap[MenuItemType.MoveToFocusedInbox]()) {
        items.push(
            <div
                key={'moveTo' + loc(moveToFocusedInboxText)}
                className={nonIconClassName}
                data-is-focusable={true}
                onClick={() => {
                    lazyTriageActions.lazyMoveMailListRowsToFocusedOrOther.importAndExecute(
                        [...tableView.selectedRowKeys.keys()],
                        tableView.id,
                        FocusedViewFilter.Focused,
                        actionSource
                    );
                    onAfterMenuClicked();
                }}>
                <span className={styles.moveToFocusedOtherMenuItems}>
                    {loc(moveToFocusedInboxText)}
                </span>
            </div>
        );
    } else if (shouldShowBehaviorMap[MenuItemType.MoveToOtherInbox]()) {
        items.push(
            <div
                key={'moveTo' + loc(moveToOtherInboxText)}
                className={nonIconClassName}
                aria-label={loc(moveToOtherInboxText)}
                role={'menuitem'}
                data-is-focusable={true}
                onClick={() => {
                    lazyTriageActions.lazyMoveMailListRowsToFocusedOrOther.importAndExecute(
                        [...tableView.selectedRowKeys.keys()],
                        tableView.id,
                        FocusedViewFilter.Other,
                        actionSource
                    );
                    onAfterMenuClicked();
                }}>
                <span className={styles.moveToFocusedOtherMenuItems}>
                    {loc(moveToOtherInboxText)}
                </span>
            </div>
        );
    }

    // Add always move to focused other
    if (shouldShowBehaviorMap[MenuItemType.AlwaysMoveToFocusedInbox]()) {
        items.push(
            <div
                key={'moveTo' + loc(alwaysMoveToFocusedInboxText)}
                className={nonIconClassName}
                data-is-focusable={true}
                onClick={() => {
                    lazyTriageActions.lazyAlwaysMoveRowToFocusedOrOther.importAndExecute(
                        [...tableView.selectedRowKeys.keys()],
                        tableView,
                        FocusedViewFilter.Focused,
                        actionSource
                    );

                    onAfterMenuClicked();
                }}>
                <span className={styles.moveToFocusedOtherMenuItems}>
                    {loc(alwaysMoveToFocusedInboxText)}
                </span>
            </div>
        );
    } else if (shouldShowBehaviorMap[MenuItemType.AlwaysMoveToOtherInbox]()) {
        items.push(
            <div
                key={'moveTo' + loc(alwaysMoveToOtherInboxText)}
                className={nonIconClassName}
                aria-label={loc(alwaysMoveToOtherInboxText)}
                role={'menuitem'}
                data-is-focusable={true}
                onClick={() => {
                    lazyTriageActions.lazyAlwaysMoveRowToFocusedOrOther.importAndExecute(
                        [...tableView.selectedRowKeys.keys()],
                        tableView,
                        FocusedViewFilter.Other,
                        actionSource
                    );

                    onAfterMenuClicked();
                }}>
                <span className={styles.moveToFocusedOtherMenuItems}>
                    {loc(alwaysMoveToOtherInboxText)}
                </span>
            </div>
        );
    }

    return items;
}

/**
 * Gets custom menu items for the move to context menu.
 * Custom items will be added to the end of the menu.
 * @param tableView tableView where the move is being performed
 * @param shouldShowBehaviorMap the behavior map used to determine which items to show
 */
function getSweepMenuItem(
    tableView: TableView,
    shouldShowBehaviorMap: { [id: number]: ShouldShowBehavior }
): MoveToMenuItem[] {
    return shouldShowBehaviorMap[MenuItemType.Sweep]()
        ? [
              {
                  title: loc(moveToFolderMoveAllFrom),
                  displayText: loc(moveToFolderMoveAllFrom),
                  onClick: () => {
                      lazyShowSweepDialog.importAndExecute(
                          [...tableView.selectedRowKeys.keys()],
                          tableView
                      );
                  },
              },
          ]
        : null;
}

/**
 * Moves given rows to a specified folder
 * @param tableView tableView where the move is being performed
 * @param destinationFolderId - the destination folder
 * @param rowKeysToMove the list of rowKeys which will be moved
 */
function moveRows(
    tableViewId: string,
    rowKeysToMove: string[],
    destinationFolderId: string,
    actionSource: ActionSource
): void {
    lazyTriageActions.lazyMoveMailListRows.importAndExecute(
        [...rowKeysToMove],
        destinationFolderId,
        tableViewId,
        actionSource
    );
}

/**
 * Gets the moveto folder menu items for command bar
 * @param tableView the tableView
 * @param actionSource the actionSource
 * @param shouldShowBehaviorMap the behavior map to determine the triage action behavior in a current context
 * @return returns the contextual menu items
 */
export function getMoveToPropertiesForCommandBar(
    tableView: TableView,
    shouldShowBehaviorMap: { [id: number]: ShouldShowBehavior }
) {
    return getMoveToMenuProperties(
        tableView,
        true /* shouldShowSearchBox */,
        () => resetFocus(), // always reset focus to dismiss the menu from commanding bar
        'CommandBar',
        shouldShowBehaviorMap,
        DirectionalHint.bottomLeftEdge,
        true /* directionalHintFixed */
    );
}

/**
 * Gets the moveto folder menu items for context menu
 * @param tableViewId the tableView id
 * @param dismissMenu the callback to be called to dismiss of menu
 * @param shouldShowBehaviorMap the behavior map to determine the triage action behavior in a current context
 * @param directionalHint determines which direction the callout appears
 * @param rowKeysToMove the list of rowKeys which will be moved
 * @return returns the contextual menu items
 */
export function getMoveToPropertiesForContextMenu(
    tableViewId: string,
    dismissMenu: (ev?: any) => void,
    actionSource: ActionSource,
    shouldShowBehaviorMap: { [id: number]: ShouldShowBehavior },
    directionalHint: DirectionalHint,
    rowKeysToMove?: string[],
    shouldShowSearchBox?: boolean
): IContextualMenuProps {
    const tableView = listViewStore.tableViews.get(tableViewId);
    return getMoveToMenuProperties(
        tableView,
        shouldShowSearchBox,
        dismissMenu, // Callback to dismiss the right click context menu
        actionSource,
        shouldShowBehaviorMap,
        directionalHint,
        false /* directionalHintFixed */,
        rowKeysToMove
    );
}

/**
 * Gets the move to folder menu items
 * @param tableView the tableView
 * @param shouldShowSearchBox flag indicating whether to show search box or not
 * @param dismissMenu callback to be called to dismiss the move to menu context menu
 * @param actionSource the actionSource
 * @param actionSource the move to menu action source
 * @param shouldShowBehaviorMap the behavior map to determine the triage action behavior in a current context
 * @param directionalHint determines which direction the callout appears
 * @param directionalHintFixed determines whether to keep the direction of the menu same regardless of the space constraints.
 * @param rowKeysToMove the list of rowKeys which will be moved
 * @return returns the contextual menu items
 */
function getMoveToMenuProperties(
    tableView: TableView,
    shouldShowSearchBox: boolean,
    dismissMenu: () => void,
    actionSource: ActionSource,
    shouldShowBehaviorMap: { [id: number]: ShouldShowBehavior },
    directionalHint?: DirectionalHint,
    directionalHintFixed?: boolean,
    rowKeysToMove?: string[]
): IContextualMenuProps {
    const supportedMoveToMailboxContexts: MoveToMailboxContext[] = ['primary', 'archive'];
    if (isFeatureEnabled('tri-sharedFolders')) {
        supportedMoveToMailboxContexts.push('sharedFolder');
    }
    const moveToMenuProps = getMoveToMenuProps({
        supportedMoveToMailboxContexts,
        shouldShowSearchBox: shouldShowSearchBox,
        actionSource: actionSource,
        createNewFolder: createNewFolder,
        dismissMenu: dismissMenu,
        onFolderClick: folderId => {
            if (isItemPartOperation()) {
                lazyTriageActions.lazyMoveItemsBasedOnNodeIds
                    .import()
                    .then(moveItemsBasedOnNodeIds => {
                        moveItemsBasedOnNodeIds(
                            listViewStore.expandedConversationViewState.selectedNodeIds,
                            folderId,
                            tableView.id,
                            actionSource
                        );
                    });
            } else {
                // Check if rowKeys are explicitly passed (ex: in the case of moveTo quick action). In this case,
                // the move operation will be performed on the passed 'rowKeysToMove'.
                moveRows(
                    tableView.id,
                    rowKeysToMove || [...tableView.selectedRowKeys.keys()],
                    folderId,
                    actionSource
                );
            }
        },
        getMenuItemsToPrepend: (nonIconClassName, iconClassName, onAfterMenuClicked) =>
            getInboxMenuItems(
                tableView,
                nonIconClassName,
                iconClassName,
                onAfterMenuClicked,
                actionSource,
                shouldShowBehaviorMap
            ),
        getCustomIcon: getCustomIcon,
        directionalHint: directionalHint,
        directionalHintFixed: directionalHintFixed,
        getCustomMenuItemsToAppend: () => getSweepMenuItem(tableView, shouldShowBehaviorMap),
        width: MOVETO_WIDTH,
    });
    return moveToMenuProps;
}
